import React, { useCallback, useState, useEffect } from 'react';
import sortBy from 'lodash/sortBy';
import isNumber from 'lodash/isNumber';
import classNameBind from 'classnames/bind';
import { withTranslation } from '../Translator';
import Message from '../Message';
import Stack from '../Stack';
import Icon from '../Icon';
import Tooltip from '../Tooltip';
import SortableHeader from '../List/components/SortableHeader';
import styles from './DataTable.module.css';

/* eslint-disable react/no-array-index-key */

const DataTable = ({
  columns,
  data,
  zebra,
  fullWidth,
  fullHeight,
  toolbar,
  extraHeaders,
  t,
  message,
  messageType,
  height,
  headerLikeToolbar,
  additionalMessage,
  appearance,
  toolbarHeight,
} = {}) => {
  const [transformsMap, setTransformsMap] = useState({});
  const [dataClone, setDataClone] = useState(data);
  let nodes = {};

  const measuredRef = useCallback((node, rowIndex) => {
    if (node !== null && !transformsMap[0] && isNumber(data[0].moveTo)) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      nodes = { ...nodes, [rowIndex]: node };

      if (rowIndex === (data.length - 1) && !transformsMap[0]) {
        let dataSorted = data.map((el, index) => ({ ...el, oldRowIndex: index }));
        dataSorted = sortBy(dataSorted, el => el.moveTo);

        const transformsMapTmp = {};
        let nextPosition = nodes[0].offsetTop - 1;

        for (let newRowIndex = 0; newRowIndex < dataSorted.length; newRowIndex++) {
          const oldRowIndex = dataSorted[newRowIndex].oldRowIndex;
          transformsMapTmp[oldRowIndex] = nextPosition - nodes[oldRowIndex].offsetTop;
          nextPosition += nodes[oldRowIndex].offsetHeight;
        }

        setTransformsMap(transformsMapTmp);
      }
    }
  }, [transformsMap]);

  useEffect(() => {
    setTransformsMap({});
    setDataClone(data);
  }, [data]);

  const bindClasses = classNameBind.bind(styles);

  const renderHeaderCell = (columnObj, colIndex, secondaryColIndex = null, isSecondary = false, isTertiary = false) => {
    let rowSpan;
    let sortableHeader;
    const sortable = columnObj.sortable || {};
    const isSortable = Boolean(sortable.onClick);

    if (columnObj.columns || isTertiary) {
      rowSpan = 1;
    } else {
      rowSpan = isSecondary ? 2 : 3;
    }

    if (isSortable) {
      sortableHeader = appearance === 'default'
        ? (
          <Stack verticalAlignment="center">
            {sortable.iconLeft && (
              <Stack.Item>
                <Icon name={sortable.iconLeft} color={sortable.selected ? 'blue' : 'default'} />
              </Stack.Item>
            )}
            <Stack.Item fill previousSpacing={sortable.iconLeft ? 'narrow' : 'default'}>
              <div className={bindClasses('text-wrapper')}>
                {columnObj.header}
              </div>
            </Stack.Item>
            <Stack.Item>
              <div className={bindClasses('icon-wrapper')}>
                <Icon
                  name={sortable.order ? { asc: 'sort-ascending', dsc: 'sort-descending' }[sortable.order] : 'sort-2'}
                  color={sortable.selected ? 'blue' : 'gray'}
                />
              </div>
            </Stack.Item>
          </Stack>
        )
        : (<SortableHeader {...sortable} text={columnObj.header} allowMultiline={!sortable.breakLongContent} />);
    }

    return (
      <Tooltip {...columnObj.tooltip}>
        <th
          key={`${colIndex}_${secondaryColIndex}_${columnObj.accessor || (isSecondary ? 'secondaryParent' : 'parent')}`}
          colSpan={columnObj.columns ? (columnObj.columns.reduce((total, colObj) => (total + (colObj.columns ? colObj.columns.length : 1)), 0)) : 1}
          rowSpan={rowSpan}
          className={bindClasses(
            {
              styled: typeof (columnObj.header) !== 'object',
              forcedMinWidth: columnObj.forcedMinWidth,
              invisible: colIndex === 0 && !isSecondary && !isTertiary && !columnObj.header,
              sortable: isSortable,
              'sortable-disabled': sortable.disabled,
              'sortable-selected': sortable.selected,
              'sortable-breakLongContent': sortable.breakLongContent,
            },
            `text-align-${columnObj.textAlign || 'left'}`,
            `padding-${columnObj.padding}`,
            `borderForBorderlessAppearance-${columnObj.borderForBorderlessAppearance}`,
          )}
          data-ppuc-id="DataTable.headerCell"
          onClick={appearance === 'default' && isSortable && !sortable.disabled ? sortable.onClick : undefined}
        >
          {
            isSortable
              ? sortableHeader
              : columnObj.header
          }
        </th>
      </Tooltip>
    );
  };

  const renderDataCell = (rowData, columnObj, rowIndex, extraHeadersNumber) => (
    rowData[columnObj.accessor] ? (
      <td
        key={`${columnObj.accessor}_${rowIndex}`}
        className={bindClasses(
          {
            styled: typeof (rowData[columnObj.accessor].content) !== 'object',
            bold: rowData[columnObj.accessor].bold,
            'extra-header-bg': rowIndex < extraHeadersNumber,
            'row-darker': rowData[columnObj.accessor].darker,
            'animated-highlight': rowData[columnObj.accessor].highlight,
          },
          `text-align-${rowData[columnObj.accessor].textAlign || 'left'}`,
          `padding-${rowData[columnObj.accessor].padding}`,
          `borderForBorderlessAppearance-${rowData[columnObj.accessor].borderForBorderlessAppearance}`,
        )}
        rowSpan={rowData[columnObj.accessor].rowSpan}
        colSpan={rowData[columnObj.accessor].colSpan}
        data-ppuc-id="DataTable.dataCell"
      >
        {rowData[columnObj.accessor].content}
      </td>
    ) : null
  );

  const countColumns = () => (
    columns.reduce((total, colObj) => (
      total + (colObj.columns ? colObj.columns.reduce((total2, colObj2) => (
        total2 + (colObj2.columns ? colObj2.columns.length : 1)
      ), 0) : 1)
    ), 0)
  );

  const renderZeroState = () => (
    <tr>
      <td
        className={bindClasses('zero-state')}
        colSpan={countColumns()}
        data-ppuc-id="DataTable.zeroState"
      >
        <Message message={t('noDataAvaiable')} messageType="subdued" centerHorizontal />
      </td>
    </tr>
  );

  const renderMessage = () => (
    message
      ? (
        <tr>
          <td
            className={bindClasses('message')}
            colSpan={countColumns()}
            data-ppuc-id="DataTable.message"
          >
            <Message
              messageType={messageType}
              message={message}
              centerHorizontal
              additionalMessage={additionalMessage}
            />
          </td>
        </tr>
      )
      : renderZeroState()
  );

  let headerHeightPercent = null;

  if (fullHeight && dataClone && dataClone.length) {
    const numberOfHeaders = 1
      + Number(!!toolbar)
      + Number(!!headerLikeToolbar)
      + Number(columns.filter(col => col.columns && col.columns.length).length > 0)
      + Number(columns.filter(col => col.columns && col.columns.filter(colSec => colSec.columns && colSec.columns.length)).length > 0);

    headerHeightPercent = Math.ceil((100 / (dataClone.length + numberOfHeaders)) * numberOfHeaders);
  }

  return (
    <table
      className={bindClasses('data-table', appearance, { zebra, fullWidth })}
      style={fullHeight ? { height: '100%' } : { height }}
      data-ppuc-id="DataTable"
    >
      <thead
        className={bindClasses({ narrow: !!columns.find(col => col.columns) })}
        data-ppuc-id="DataTable.head"
        style={headerHeightPercent ? { height: `${headerHeightPercent}%` } : {}}
      >
        {toolbar && (
          <tr
            className={bindClasses('top-panel', toolbarHeight)}
            data-ppuc-id="DataTable.toolbar"
          >
            <th colSpan={countColumns()}>
              {toolbar}
            </th>
          </tr>
        )}
        {headerLikeToolbar && (
          <tr style={height !== 'initial' && !fullHeight ? { height: '1px' } : {}}>
            <th
              colSpan={countColumns() - headerLikeToolbar.rightElementWidthInCols}
              className={bindClasses('padding-half')}
            >
              {headerLikeToolbar.contentLeft}
            </th>
            <th colSpan={headerLikeToolbar.rightElementWidthInCols} className={bindClasses('padding-half')}>
              {headerLikeToolbar.contentRight}
            </th>
          </tr>
        )}
        <tr style={height !== 'initial' && !fullHeight ? { height: '1px' } : {}}>
          {columns.map((colObj, colIndex) => (
            renderHeaderCell(colObj, colIndex)
          ))}
        </tr>
        <tr style={height !== 'initial' && !fullHeight ? { height: '1px' } : {}}>
          {columns.map((colObj, colIndex) => (colObj.columns
            ? colObj.columns.map((secondaryColObj, secondaryColIndex) => (
              renderHeaderCell(secondaryColObj, colIndex, secondaryColIndex, true)
            )) : null))}
        </tr>
        <tr style={height !== 'initial' && !fullHeight ? { height: '1px' } : {}}>
          {columns.map((colObj, colIndex) => (colObj.columns
            ? colObj.columns.map((secondaryColObj, secondaryColIndex) => (secondaryColObj.columns
              ? secondaryColObj.columns.map(tertiaryColObj => (
                renderHeaderCell(tertiaryColObj, colIndex, secondaryColIndex, false, true)
              )) : null
            )) : null))}
        </tr>
        <tr />
      </thead>
      <tbody
        data-ppuc-id="DataTable.body"
        style={headerHeightPercent ? { height: `${100 - headerHeightPercent}%` } : {}}
      >
        {!message && dataClone && dataClone.length ? dataClone.map((rowData, rowIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr
            key={rowIndex}
            data-ppuc-id="DataTable.row"
            ref={ref => measuredRef(ref, rowIndex)}
            className={bindClasses({ 'translate-transform': transformsMap[rowIndex], hiddenMoveTo: rowData.hiddenMoveTo })}
            style={{
              transform: transformsMap[rowIndex] ? `translateY(${transformsMap[rowIndex] + 1}px)` : '',
            }}
          >
            {columns.map(colObj => (colObj.columns
              ? colObj.columns.map(secondaryColObj => (secondaryColObj.columns
                ? secondaryColObj.columns.map(tertiaryColObj => (
                  renderDataCell(rowData, tertiaryColObj, rowIndex, extraHeaders)
                )) : (
                  renderDataCell(rowData, secondaryColObj, rowIndex, extraHeaders)
                )
              )) : (
                renderDataCell(rowData, colObj, rowIndex, extraHeaders)
              )
            ))}
          </tr>
        )) : renderMessage()}
      </tbody>
    </table>
  );
};

DataTable.defaultProps = {
  columns: [],
  data: [],
  zebra: false,
  fullWidth: false,
  toolbar: null,
  extraHeaders: 0,
  message: '',
  messageType: 'error',
  height: 'initial',
  headerLikeToolbar: null,
  additionalMessage: null,
  appearance: 'default',
  toolbarHeight: 'default',
};

const enhanced = withTranslation('components')(DataTable);
enhanced.displayName = 'DataTable';

export default enhanced;
export { DataTable };
